import type { Icon, DefaultIcon, DotIcon, LiveIcon, PinImageIcon, RoundImageIcon, SignIcon, RendererType } from "@/shared/Icons";
import type { TFeature } from "@/shared/types/geojson";
import { loadImage as browserLoadImage, rasterImage as browserRasterImage } from "@/utils/ImageCache";
import { addRoundRectPath, dropShadow } from "./base/RenderUtils";
import { DEFAULT_SIZE_STOPS, interpolateStops } from "@/shared/utils/iconSize";

export interface RenderOptions {
	variation?: string;
	text?: string;
	/** Render the bare icon (no shadow padding; default renderer skips its circular background) for tiles/legends. */
	bare?: boolean;
}

export interface MarkerRenderLayout {
	scaleFactor: number;
	scale: number;
	imageWidth: number;
	imageHeight: number;
	offsetX: number;
	offsetY: number;
}

// Overridable image loading — server-side code can replace these
let _loadImage: (src: string) => Promise<HTMLImageElement> = browserLoadImage;
let _rasterImage: (img: HTMLImageElement, w: number, h: number) => HTMLCanvasElement | HTMLImageElement = browserRasterImage;

/** Override the image loader (e.g. for server-side rendering with skia-canvas). */
export function setImageLoader(
	loadImage: (src: string) => Promise<HTMLImageElement>,
	rasterImage: (img: HTMLImageElement, w: number, h: number) => HTMLCanvasElement | HTMLImageElement,
) {
	_loadImage = loadImage;
	_rasterImage = rasterImage;
}

const PIXEL_RATIO = 2;

/** Logical px of transparent margin reserved around a marker for its drop shadow. */
const MARKER_PADDING = 6;

/** Pixel ratio for image-based markers — slightly lower on non-retina to save memory. */
function imagePixelRatio(): number {
	return window.devicePixelRatio > 1 ? 2 : 1.5;
}

/** Compute the canvas dimensions for a square marker, including shadow padding unless `bare`. */
function squareCanvasSize(baseSize: number, pr: number, bare: boolean) {
	const markerW = baseSize * pr;
	const padding = bare ? 0 : MARKER_PADDING * pr;
	const size = markerW + padding;
	return { markerW, padding, cw: size, ch: size };
}

/** Compute the canvas dimensions for a rectangular marker, including shadow padding unless `bare`. */
function rectCanvasSize(baseW: number, baseH: number, pr: number, bare: boolean) {
	const markerW = baseW * pr;
	const markerH = baseH * pr;
	const padding = bare ? 0 : MARKER_PADDING * pr;
	return { markerW, markerH, padding, cw: markerW + padding, ch: markerH + padding };
}

type MarkerCanvasSize = { width: number; height: number };

type RendererIconMap = {
	default: DefaultIcon;
	dot: DotIcon;
	live: LiveIcon;
	sign: SignIcon;
	roundImage: RoundImageIcon;
	pinImage: PinImageIcon;
};

interface MarkerRenderer<TIcon extends Icon = Icon> {
	getCanvasSize(icon: TIcon, bare: boolean): MarkerCanvasSize;
	getCacheKey(icon: TIcon, color: string, opts?: RenderOptions): string;
	render(icon: TIcon, color: string, opts?: RenderOptions): Promise<HTMLCanvasElement>;
	getOffset?(icon: TIcon, scale: number): [number, number];
}

type MarkerRendererRegistry = {
	[K in keyof RendererIconMap]: MarkerRenderer<RendererIconMap[K]>;
};

function createMarkerRenderer<TIcon extends Icon>(renderer: MarkerRenderer<TIcon>): MarkerRenderer<TIcon> {
	return renderer;
}

export function getMarkerScaleFactor(zoom: number): number {
	return interpolateStops(DEFAULT_SIZE_STOPS, zoom);
}

const DEFAULT_SIZE = 35;

const defaultMarkerRenderer = createMarkerRenderer<DefaultIcon>({
	getCanvasSize(_icon, bare) {
		const { cw, ch } = squareCanvasSize(DEFAULT_SIZE, PIXEL_RATIO, bare);
		return { width: cw, height: ch };
	},
	getCacheKey(icon, color, opts) {
		const prefix = opts?.bare ? "bare:" : "";
		return `${prefix}default:${icon.fixedColor ?? color}:${icon.asset}:${icon.scale}:${icon.lineWidth ?? 3}:${opts?.variation ?? ""}`;
	},
	async render(icon, color, opts) {
		const width = DEFAULT_SIZE;
		const pr = PIXEL_RATIO;

		const fillColor = icon.fixedColor ?? color;
		const lineColor = "#ffffff";
		const lineWidth = icon.lineWidth ?? 3;
		const invert = opts?.variation === "invert";
		const iconPath = `/icons/${icon.asset}`;

		const { markerW, cw, ch } = squareCanvasSize(width, pr, !!opts?.bare);

		const [canvas, ctx] = createCanvas(cw, ch);

		if (!opts?.bare) {
			ctx.beginPath();
			ctx.arc(cw / 2, ch / 2, markerW / 2, 0, Math.PI * 2);
			ctx.fillStyle = invert ? fillColor : lineColor;
			if (!invert) dropShadow(ctx);
			ctx.fill();

			ctx.fillStyle = invert ? lineColor : fillColor;
			ctx.shadowColor = "transparent";
			ctx.beginPath();
			ctx.arc(cw / 2, ch / 2, markerW / 2 - lineWidth * pr, 0, Math.PI * 2);
			ctx.fill();
			ctx.save();
			ctx.clip();
		}

		try {
			const image = await _loadImage(iconPath);
			const largest = Math.max(image.width, image.height);
			const scale = ((width - 7 - lineWidth) * pr) / largest * icon.scale;
			const imgW = image.width * scale;
			const imgH = image.height * scale;
			const x = cw / 2 - imgW / 2;
			const y = ch / 2 - imgH / 2;

			if (invert) {
				const [tmpCanvas, tmpCtx] = createCanvas(cw, ch);
				tmpCtx.drawImage(image, x, y, imgW, imgH);
				tmpCtx.globalCompositeOperation = "source-in";
				tmpCtx.fillStyle = fillColor;
				tmpCtx.fillRect(0, 0, cw, ch);
				ctx.drawImage(tmpCanvas, 0, 0);
			} else {
				ctx.drawImage(image, x, y, imgW, imgH);
			}
		} catch {
			// Icon failed to load — render without icon
		}

		if (!opts?.bare) {
			ctx.restore();
		}

		return canvas;
	},
});

const DOT_SIZE = 14;

const dotMarkerRenderer = createMarkerRenderer<DotIcon>({
	getCanvasSize(icon, bare) {
		const { cw, ch } = squareCanvasSize(icon.width ?? DOT_SIZE, PIXEL_RATIO, bare);
		return { width: cw, height: ch };
	},
	getCacheKey(icon, color, opts) {
		const prefix = opts?.bare ? "bare:" : "";
		return `${prefix}dot:${icon.fixedColor ?? color}:${icon.width ?? DOT_SIZE}`;
	},
	async render(icon, color, opts) {
		const width = icon.width ?? DOT_SIZE;
		const pr = PIXEL_RATIO;
		const { markerW, cw, ch } = squareCanvasSize(width, pr, !!opts?.bare);

		const [canvas, ctx] = createCanvas(cw, ch);
		const fillColor = icon.fixedColor ?? color;

		ctx.beginPath();
		ctx.arc(cw / 2, ch / 2, markerW / 2, 0, Math.PI * 2);
		if (!opts?.bare) dropShadow(ctx);
		ctx.fillStyle = fillColor;
		ctx.fill();

		return canvas;
	},
});

const LIVE_SIZE = 32;
const LIVE_BORDER = 3;

const liveMarkerRenderer = createMarkerRenderer<LiveIcon>({
	getCanvasSize(_icon, bare) {
		const { cw, ch } = squareCanvasSize(LIVE_SIZE + LIVE_BORDER, PIXEL_RATIO, bare);
		return { width: cw, height: ch };
	},
	getCacheKey(_icon, color, opts) {
		const prefix = opts?.bare ? "bare:" : "";
		return `${prefix}live:${color}:${opts?.text ?? ""}`;
	},
	async render(_icon, color, opts) {
		const pr = PIXEL_RATIO;
		const lineColor = "#ffffff";
		const lineWidth = LIVE_BORDER;
		const { markerW, cw, ch } = squareCanvasSize(LIVE_SIZE + lineWidth, pr, !!opts?.bare);

		const [canvas, ctx] = createCanvas(cw, ch);

		if (!opts?.bare) {
			ctx.beginPath();
			ctx.arc(cw / 2, ch / 2, markerW / 2, 0, Math.PI * 2);
			ctx.fillStyle = lineColor;
			dropShadow(ctx);
			ctx.fill();

			ctx.fillStyle = color;
			ctx.shadowColor = "transparent";
			ctx.beginPath();
			ctx.arc(cw / 2, ch / 2, markerW / 2 - lineWidth * pr, 0, Math.PI * 2);
			ctx.fill();
		}

		ctx.fillStyle = "#fff";
		ctx.font = `${16 * pr}px Poppins`;
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(opts?.text ?? "XX", cw / 2, ch / 2 + 3);

		return canvas;
	},
});

const SIGN_SIZE = 32;

const signMarkerRenderer = createMarkerRenderer<SignIcon>({
	getCanvasSize(_icon, bare) {
		const { cw, ch } = squareCanvasSize(SIGN_SIZE, PIXEL_RATIO, bare);
		return { width: cw, height: ch };
	},
	getCacheKey(icon, color, opts) {
		const prefix = opts?.bare ? "bare:" : "";
		return `${prefix}sign:${color}:${icon.asset}:${icon.letter ?? ""}:${icon.circle ?? ""}`;
	},
	async render(icon, color, opts) {
		const width = SIGN_SIZE;
		const pr = PIXEL_RATIO;
		const { markerW, cw, ch } = squareCanvasSize(width, pr, !!opts?.bare);

		const [canvas, ctx] = createCanvas(cw, ch);

		ctx.beginPath();
		ctx.fillStyle = color;
		addRoundRectPath(ctx, (cw - markerW) / 2, (ch - markerW) / 2, markerW, markerW, 4 * pr);
		if (!opts?.bare) dropShadow(ctx);
		ctx.fill();
		ctx.shadowColor = "transparent";

		if (icon.letter) {
			if (icon.circle) {
				ctx.beginPath();
				ctx.fillStyle = "#fff";
				ctx.arc(cw / 2, markerW / 2 + (ch - markerW) / 2, markerW / 2 - 2 * pr, 0, 2 * Math.PI);
				ctx.fill();
			}
			ctx.fillStyle = icon.circle ? color : "#fff";
			ctx.font = `${28 * pr}px Poppins`;
			ctx.textAlign = "center";
			ctx.fillText(icon.letter, cw / 2, markerW / 1.22 + (ch - markerW) / 2);
		} else {
			try {
				const image = await _loadImage(`/icons/${icon.asset}`);
				const largest = Math.max(image.width, image.height);
				const scale = Math.min(((width - 5) * pr) / largest, 5);
				const x = cw / 2 - (image.width * scale) / 2;
				const y = ch / 2 - (image.height * scale) / 2;
				ctx.drawImage(image, x, y, image.width * scale, image.height * scale);
			} catch {
				// Icon failed to load
			}
		}

		return canvas;
	},
});

const ROUND_IMAGE_SIZE = 32;

const roundImageMarkerRenderer = createMarkerRenderer<RoundImageIcon>({
	getCanvasSize(icon, bare) {
		const { cw, ch } = squareCanvasSize(icon.width ?? ROUND_IMAGE_SIZE, PIXEL_RATIO, bare);
		return { width: cw, height: ch };
	},
	getCacheKey(icon, color, opts) {
		const prefix = opts?.bare ? "bare:" : "";
		return `${prefix}round:${icon.asset}:${icon.width ?? ROUND_IMAGE_SIZE}:${icon.scale}:${color}`;
	},
	async render(icon, color, opts) {
		const width = icon.width ?? ROUND_IMAGE_SIZE;
		const pr = imagePixelRatio();
		const { cw, ch } = squareCanvasSize(width, pr, !!opts?.bare);

		const [canvas, ctx] = createCanvas(cw, ch);

		try {
			const image = await _loadImage(`/icons/${icon.asset}`);
			const largest = Math.max(image.width, image.height);
			const scale = Math.min((width * pr) / largest, 5) * icon.scale;
			const x = cw / 2 - (image.width * scale) / 2;
			const y = ch / 2 - (image.height * scale) / 2;
			if (!opts?.bare) dropShadow(ctx);
			const rasterized = _rasterImage(image, image.width * scale, image.height * scale);
			ctx.drawImage(rasterized, x, y, image.width * scale, image.height * scale);
		} catch {
			// Image failed to load — render empty
		}

		return canvas;
	},
	getOffset(icon, scale) {
		return [0, (icon.offsetY ?? 0) * scale];
	},
});

const PIN_WIDTH = 32;
const PIN_HEIGHT = 48;

const pinImageMarkerRenderer = createMarkerRenderer<PinImageIcon>({
	getCanvasSize(_icon, bare) {
		const { cw, ch } = rectCanvasSize(PIN_WIDTH, PIN_HEIGHT, PIXEL_RATIO, bare);
		return { width: cw, height: ch };
	},
	getCacheKey(_icon, color, opts) {
		const prefix = opts?.bare ? "bare:" : "";
		return `${prefix}pin:${_icon.asset}:${_icon.scale}:${color}`;
	},
	async render(icon, color, opts) {
		const width = PIN_WIDTH;
		const pr = PIXEL_RATIO;
		const { cw, ch } = rectCanvasSize(width, PIN_HEIGHT, pr, !!opts?.bare);

		const [canvas, ctx] = createCanvas(cw, ch);

		try {
			const image = await _loadImage("/icons/icons/mapmarker.svg");
			const scale = Math.min((width * pr) / image.width, 5) * icon.scale;

			ctx.fillStyle = color;
			ctx.fillRect(20, 20, 40, 40);

			const x = cw / 2 - (image.width * scale) / 2;
			const y = ch / 2 - (image.height * scale) / 2;
			if (!opts?.bare) dropShadow(ctx);
			const rasterized = _rasterImage(image, image.width * scale, image.height * scale);
			ctx.drawImage(rasterized, x, y, image.width * scale, image.height * scale);
		} catch {
			// Image failed to load
		}

		return canvas;
	},
	getOffset(_icon, scale) {
		return [0, 5 * scale];
	},
});

const markerRenderers = {
	default: defaultMarkerRenderer,
	dot: dotMarkerRenderer,
	live: liveMarkerRenderer,
	sign: signMarkerRenderer,
	roundImage: roundImageMarkerRenderer,
	pinImage: pinImageMarkerRenderer,
} satisfies MarkerRendererRegistry;

function getRendererType(icon: Icon): RendererType {
	return icon.renderer ?? "default";
}

function getMarkerRenderer(icon: Icon): MarkerRenderer {
	const rendererType = getRendererType(icon);
	return markerRenderers[rendererType];
}

function getMarkerCanvasSize(icon: Icon, bare = false): { width: number; height: number } {
	return getMarkerRenderer(icon).getCanvasSize(icon, bare);
}

function getAnchorOffset(anchor: string, imageWidth: number, imageHeight: number, offsetX = 0, offsetY = 0): [number, number] {
	let x = anchor.includes("left") ? imageWidth / 2 : anchor.includes("right") ? -imageWidth / 2 : 0;
	let y = anchor.includes("top") ? imageHeight / 2 : anchor.includes("bottom") ? -imageHeight / 2 : 0;
	x += offsetX;
	y += offsetY;
	if (anchor.includes("-")) {
		x /= 1.414;
		y /= 1.414;
	}
	return [x, y];
}

export function getMarkerRenderLayout(marker: TFeature, icon: Icon, zoom: number): MarkerRenderLayout {
	const scaleFactor = getMarkerScaleFactor(zoom);
	const scale = 0.5 * scaleFactor;
	const canvasSize = getMarkerCanvasSize(icon, false);
	const imageWidth = canvasSize.width * scale;
	const imageHeight = canvasSize.height * scale;
	const [iconOffsetX, iconOffsetY] = getMarkerIconOffset(icon, scaleFactor);
	const [offsetX, offsetY] = getAnchorOffset(marker.properties?.anchor ?? "center", imageWidth, imageHeight, iconOffsetX, iconOffsetY);

	return {
		scaleFactor,
		scale,
		imageWidth,
		imageHeight,
		offsetX,
		offsetY,
	};
}

/** Build a cache key that captures all visually-significant properties for a given icon + options. */
export function getMarkerIconCacheKey(icon: Icon, color: string, opts?: RenderOptions): string {
	return getMarkerRenderer(icon).getCacheKey(icon, color, opts);
}

// ── Image cache ─────────────────────────────────────────────────────────

const _cache = new Map<string, HTMLCanvasElement>();
const _pending = new Map<string, { promise: Promise<HTMLCanvasElement>; callbacks: Set<() => void> }>();

/**
 * Get a cached marker icon canvas, or undefined if not yet rendered.
 * Triggers an async render if the icon is not cached and calls `onReady` once when done.
 */
export function getCachedMarkerIcon(icon: Icon, color: string, opts?: RenderOptions, onReady?: () => void): HTMLCanvasElement | undefined {
	const key = getMarkerIconCacheKey(icon, color, opts);

	const cached = _cache.get(key);
	if (cached) return cached;

	const pending = _pending.get(key);
	if (pending) {
		if (onReady) pending.callbacks.add(onReady);
		return undefined;
	}

	const callbacks = new Set<() => void>();
	if (onReady) callbacks.add(onReady);

	const promise = renderMarkerIcon(icon, color, opts)
		.then(canvas => {
			_cache.set(key, canvas);
			for (const callback of callbacks) callback();
			return canvas;
		})
		.finally(() => {
			_pending.delete(key);
		});

	_pending.set(key, { promise, callbacks });

	return undefined;
}

export function getMarkerIconOffset(icon: Icon, scale = 1): [number, number] {
	return getMarkerRenderer(icon).getOffset?.(icon, scale) ?? [0, 0];
}

/**
 * Render a marker icon to a canvas. Returns a canvas that can be drawn directly
 * with ctx.drawImage(). Results are NOT automatically cached — use getCachedMarkerIcon()
 * for caching, or call this directly for one-off renders (e.g., MarkerDisplay tiles).
 */
export async function renderMarkerIcon(icon: Icon, color: string, opts?: RenderOptions): Promise<HTMLCanvasElement> {
	return getMarkerRenderer(icon).render(icon, color, opts);
}

// ── Helpers ─────────────────────────────────────────────────────────────

function createCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
	const canvas = document.createElement("canvas");
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext("2d")!;
	ctx.imageSmoothingQuality = "high";
	return [canvas, ctx];
}

